<script setup lang="ts">import { computed as _computed } from 'vue';

import type { MultiplaceGift } from '@consumer/types/serializers'
import { speedbumpText } from '@consumer/constants/disclaimers'
import { faPen, faLink } from '@fortawesome/pro-regular-svg-icons'
import { asyncOpenModal } from '@consumer/services/modals'
import giftlyLogo from '@consumer/assets/images/giftly-g-logo.svg'

withDefaults(defineProps<{
  multiplaceGift: MultiplaceGift
  isEditing: boolean
  canEditMultiplaceGift?: boolean
}>(), { canEditMultiplaceGift: false, })

const createdBy = _computed(() => __props.multiplaceGift.createdBy)

const displayEditButtons = _computed(() => __props.isEditing && __props.canEditMultiplaceGift)

const createdByGiftly = _computed(() => {
  return !createdBy.value || !createdBy.value.fullName || createdBy.value.giftlyEmployee
})

const createdByDisplay = computed(() => {
  if (createdByGiftly.value) {
    return { name: 'Giftly User' }
  }

  return {
    name: createdBy.value!.fullName,
    profilePictureUrl: createdBy.value!.profilePictureUrl,
  }
})

function openInspireLink (event: any) {
  // eslint-disable-next-line no-alert
  if (confirm(speedbumpText)) {
    window.open(event.target.href, '_blank')
  }
}
</script>
<template>
  <div class="attribution flex gap-2 items-center text-v2-secondary font-medium text-lg">
    <ProfilePicture
      v-if="!createdByGiftly"
      class="rounded-v2sm flex-shrink-none"
      :size="38"
      :circle="false"
      :user="createdByDisplay"
    />
    <img
      v-else
      class="rounded-v2sm flex-shrink-none"
      height="28"
      width="28"
      :src="giftlyLogo"
      alt="Giftly"
      data-testid="attribution-giftly-logo"
    >
    <span>
      <span v-if="!createdByGiftly" data-testid="attribution-user-name">{{ createdBy!.firstName }}</span>
      <a v-else href="/" class="text-navy" data-testid="attribution-giftly">Giftly</a>
      made this gift
    </span>

    <EditButton
      v-if="displayEditButtons"
      data-testid="edit-details-button"
      @click="asyncOpenModal(
        import('@consumer/modals/multiPlaceGiftIdea/EditDetailsModal.vue'),
        { multiplaceGift },
      )"
    />

    <template v-if="multiplaceGift.inspiredByUrl">
      <div>·</div>
      <div>
        Inspired by
        <a
          :href="multiplaceGift.inspiredByUrl"
          data-testid="attribution-link"
          @click.prevent="openInspireLink"
        >
          {{ multiplaceGift.inspiredByText }}
        </a>
      </div>
    </template>

    <GIconButton
      v-if="displayEditButtons"
      :icon="multiplaceGift.inspiredByUrl ? faPen : faLink"
      label="Edit Inspired By"
      data-testid="attribution-add-link"
      @click="asyncOpenModal(
        import('@consumer/modals/multiPlaceGiftIdea/EditInspiredByModal.vue'),
        { multiplaceGift },
      )"
    />
  </div>
</template>
